import type { IEmployeeTalkState } from '@sharedInterfaces/IEmployeeTalkState';
import { WebSocketClient } from '@src/APIs/WebSockets/WebSocketClient';
import { EmployeeTalkEventDispatcher } from '@src/App/Services/EmployeeTalkEventDispatcher';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseEmployeeTalkWebSocketResult {
    talkState: IEmployeeTalkState | null;
    setTalkState: React.Dispatch<React.SetStateAction<IEmployeeTalkState | null>>;
    loading: boolean;
    error: string | null;
    setError: (msg: string | null) => void;
    dispatcherRef: React.MutableRefObject<EmployeeTalkEventDispatcher | null>;
    isDispatcherReady: () => boolean;
}

export const useEmployeeTalkWebSocket = (employeeTalkId: number): UseEmployeeTalkWebSocketResult => {
    const [talkState, setTalkState] = useState<IEmployeeTalkState | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const dispatcherRef = useRef<EmployeeTalkEventDispatcher | null>(null);
    const initializingRef = useRef<boolean>(false);
    const readyRef = useRef<boolean>(false);

    const handleWebSocketMessage = useCallback((parsedData: { type: string; data?: unknown; error?: string }) => {
        if (parsedData.type === 'EMPLOYEE_TALK_STATE_UPDATE') {
            setTalkState(parsedData.data as IEmployeeTalkState);
            setLoading(false);
        } else if (parsedData.type === 'EMPLOYEE_TALK_APPROVAL_UPDATE') {
            window.dispatchEvent(new CustomEvent('employeeTalkApprovalUpdate', { detail: parsedData.data }));
        } else if (parsedData.type === 'EMPLOYEE_TALK_ERROR') {
            console.error('❌ WebSocket Error:', parsedData.error);
            setError(parsedData.error || 'Unknown error');
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (initializingRef.current) {
            return;
        }
        initializingRef.current = true;

        if (dispatcherRef.current) {
            dispatcherRef.current.unsubscribe();
        }

        dispatcherRef.current = new EmployeeTalkEventDispatcher(employeeTalkId);

        WebSocketClient.connect()
            .then(() => {
                if (!dispatcherRef.current) return;

                dispatcherRef.current.subscribe(handleWebSocketMessage);
                dispatcherRef.current.requestState();

                readyRef.current = true;
                initializingRef.current = false;
            })
            .catch((err) => {
                console.error('❌ Failed to connect WebSocket:', err);
                setError('WebSocket connection failed');
                setLoading(false);
                initializingRef.current = false;
                readyRef.current = false;
            });

        return () => {
            if (dispatcherRef.current) {
                dispatcherRef.current.unsubscribe();
                dispatcherRef.current = null;
            }
            initializingRef.current = false;
            readyRef.current = false;
        };
    }, [employeeTalkId, handleWebSocketMessage]);

    const isDispatcherReady = useCallback(() => {
        return !!(dispatcherRef.current && readyRef.current);
    }, []);

    return {
        talkState,
        setTalkState,
        loading,
        error,
        setError,
        dispatcherRef,
        isDispatcherReady,
    };
};
